import React, { useEffect, useState } from "react";
import { FaBrain, FaPersonRunning } from "react-icons/fa6";
import { FiCheck, FiMessageCircle, FiMic, FiTrendingUp, FiUsers } from "react-icons/fi";
import Reveal, { useInView } from "../components/Reveal";
import Figures from "../components/Figure";
import PhoneMock, { useMockScreens } from "../components/PhoneMock";
import { SCENES, WELLBEING_SHOTS, useLocalizedAsset } from "../renders";
import { useSteffiCopy } from "../content";

const ICONS = {
  talk: FiMessageCircle,
  brain: FaBrain,
  move: FaPersonRunning,
  mic: FiMic,
  trend: FiTrendingUp,
  family: FiUsers,
};

/* A dashboard in its phone frame. The three Wohlbefinden screenshots are not
   shot yet, so an absent file leaves a quiet placeholder that holds the layout
   instead of a broken image. */
function Dashboard({ shot, alt, className = "" }) {
  const { WELLBEING } = useSteffiCopy();
  const localize = useLocalizedAsset();
  const s = localize(WELLBEING_SHOTS[shot]);
  const [state, setState] = useState("loading");
  const mock = useMockScreens();

  useEffect(() => {
    const image = new Image();
    image.onload = () => setState("ready");
    image.onerror = () => setState("missing");
    image.src = s.src;
  }, [s.src]);

  if (mock) {
    return (
      /* a drawn phone has no intrinsic width, so an `auto` grid column would
         collapse around it: give it a floor */
      <PhoneMock
        screen={shot}
        alt={alt}
        className={`mx-auto w-full max-w-[22rem] ${className}`}
        style={{ minWidth: "min(14rem, 60vw)" }}
      />
    );
  }

  if (state === "missing") {
    return (
      <div
        className={`mx-auto flex aspect-[820/1670] w-full max-w-[22rem] flex-col items-center justify-center gap-3 rounded-[2.75rem] border-2 border-dashed border-[color:var(--border)] px-8 text-center ${className}`}
      >
        <span className="t-eyebrow">{WELLBEING.missingShot}</span>
        <code className="text-[0.8125rem] leading-relaxed text-[color:var(--ink-3)]">
          {s.src.replace("/steffi/app/", "")}
        </code>
      </div>
    );
  }

  return (
    <img
      src={s.src}
      width={s.w}
      height={s.h}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={`mx-auto block h-auto w-full max-w-[22rem] ${className}`}
      style={{
        filter: "drop-shadow(0 44px 76px rgba(36,33,30,.22))",
        opacity: state === "ready" ? 1 : 0,
        transform: `scale(${state === "ready" ? 1 : 0.97})`,
        transition:
          "opacity 900ms cubic-bezier(.2,.8,.2,1), transform 900ms cubic-bezier(.2,.8,.2,1)",
      }}
    />
  );
}

/* „Hilfe Steffi“ → … → Verbunden, stepping through itself while on screen. */
function EmergencyFlow() {
  const { WELLBEING } = useSteffiCopy();
  const [ref, inView] = useInView({ threshold: 0.35, once: false });
  const [step, setStep] = useState(-1);
  const steps = WELLBEING.help.steps;

  useEffect(() => {
    if (!inView) {
      setStep(-1);
      return undefined;
    }
    let i = -1;
    const id = setInterval(() => {
      i = i + 1 > steps.length ? -1 : i + 1;
      setStep(i);
    }, 950);
    return () => clearInterval(id);
  }, [inView, steps.length]);

  return (
    <ol ref={ref} className="relative mt-10 flex flex-col gap-4">
      {steps.map((s, i) => {
        const on = step > i;
        const tone =
          s.tone === "done"
            ? { bg: "var(--sage)", fg: "#ffffff" }
            : s.tone === "muted"
              ? { bg: "var(--bg-2)", fg: "var(--ink-3)" }
              : { bg: "var(--accent)", fg: "#ffffff" };
        return (
          <li
            key={s.label}
            className="flex items-center gap-[1.375rem]"
            style={{
              opacity: on ? 1 : 0.32,
              transform: `translateX(${on ? 0 : -6}px)`,
              transition:
                "opacity 700ms cubic-bezier(.2,.8,.2,1), transform 700ms cubic-bezier(.2,.8,.2,1)",
            }}
          >
            <span
              aria-hidden
              className="grid h-[3.25rem] w-[3.25rem] shrink-0 place-items-center rounded-full text-[1.125rem] font-semibold transition-all duration-700"
              style={{
                transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
                background: on ? tone.bg : "var(--bg-2)",
                color: on ? tone.fg : "var(--ink-3)",
              }}
            >
              {s.tone === "done" && on ? <FiCheck className="h-5 w-5" /> : i + 1}
            </span>
            <span
              className={
                s.tone === "trigger"
                  ? "display text-[1.75rem] text-[color:var(--ink)]"
                  : "text-[1.55rem] font-medium leading-snug text-[color:var(--ink-2)]"
              }
            >
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default function WellbeingSection() {
  const { WELLBEING } = useSteffiCopy();
  /* the three activity blocks take it in turns, and the dashboard glows along */
  const [actRef, actInView] = useInView({ threshold: 0.3, once: false });
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (!actInView) return undefined;
    const id = setInterval(
      () => setActive((a) => (a + 1) % WELLBEING.activity.blocks.length),
      2600
    );
    return () => clearInterval(id);
  }, [actInView, WELLBEING.activity.blocks.length]);

  return (
    <>
      {/* ------------------------------------------- 1 · Hilfe, wenn sie zählt */}
      <section
        id="hilfe"
        className="relative overflow-hidden py-28 lg:py-40"
        style={{ background: "var(--bg)" }}
      >
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-10">
          <div className="mx-auto max-w-[46rem] text-center">
            <Reveal as="p" className="t-eyebrow">
              {WELLBEING.eyebrow}
            </Reveal>
            <Reveal
              as="h2"
              delay={80}
              className="t-section mt-6 !text-[clamp(3rem,6vw,5.5rem)] !leading-[0.98]"
            >
              {WELLBEING.help.title.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </Reveal>
            <Reveal as="p" delay={160} className="t-lead mt-6 text-[color:var(--ink-2)]">
              {WELLBEING.help.subtitle}
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 items-center gap-14 lg:mt-24 lg:grid-cols-2 lg:gap-20">
            <Reveal>
              <figure className="overflow-hidden rounded-[29px] border border-[color:var(--border)] shadow-[0_50px_90px_-64px_rgba(36,33,30,.5)]">
                <img
                  src={SCENES.emergencyHelp.src}
                  width={SCENES.emergencyHelp.w}
                  height={SCENES.emergencyHelp.h}
                  alt={WELLBEING.help.alts.scene}
                  loading="lazy"
                  decoding="async"
                  className="block h-full w-full object-cover"
                />
              </figure>
            </Reveal>

            <Reveal delay={120}>
              <div className="grid grid-cols-1 items-start gap-10 sm:grid-cols-[auto_1fr] sm:gap-12">
                <Dashboard
                  shot="emergency"
                  alt={WELLBEING.help.alts.shot}
                  className="!max-w-[18rem]"
                />
                <div>
                  <EmergencyFlow />
                  <p className="mt-10 max-w-[30rem] text-[1.3rem] leading-relaxed text-[color:var(--ink-2)]">
                    {WELLBEING.help.note}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------- 2 · Aktivität im Blick */}
      <section
        ref={actRef}
        id="aktivitaet"
        className="relative overflow-hidden py-28 lg:py-40"
        style={{ background: "var(--bg)" }}
      >
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-10">
          <div className="mx-auto max-w-[46rem] text-center">
            <Reveal as="h2" className="t-section">
              {WELLBEING.activity.title.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </Reveal>
            <Reveal as="p" delay={120} className="t-lead mt-6 text-[color:var(--ink-2)]">
              {WELLBEING.activity.subtitle}
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 items-center gap-14 lg:mt-24 lg:grid-cols-[1fr_0.85fr] lg:gap-24">
            <Reveal className="relative">
              {/* a soft highlight drifts down the dashboard with the blocks */}
              <div
                aria-hidden
                className="glow-blob pointer-events-none absolute left-1/2 h-[16rem] w-[16rem] -translate-x-1/2"
                style={{
                  top: `${18 + active * 22}%`,
                  background:
                    "radial-gradient(circle, rgba(215,180,138,.55) 0%, rgba(248,241,233,0) 70%)",
                  transition: "top 1200ms cubic-bezier(.2,.8,.2,1)",
                }}
              />
              <Dashboard
                shot="participation"
                alt={WELLBEING.activity.alt}
                className="relative !max-w-[27rem]"
              />
            </Reveal>

            <div className="flex flex-col gap-4">
              {WELLBEING.activity.blocks.map((block, i) => {
                const Icon = ICONS[block.icon] || FiMessageCircle;
                const on = active === i;
                return (
                  <Reveal key={block.title} delay={i * 110}>
                    <article
                      className="flex items-start gap-6 rounded-[1.75rem] px-8 py-7 transition-all duration-700"
                      style={{
                        transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
                        background: on ? "rgba(255,255,255,.85)" : "transparent",
                        boxShadow: on ? "0 24px 54px -34px rgba(36,33,30,.4)" : "none",
                      }}
                    >
                      <span
                        aria-hidden
                        className="grid h-14 w-14 shrink-0 place-items-center rounded-full transition-all duration-700"
                        style={{
                          transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
                          background: on ? "var(--accent)" : "var(--bg-2)",
                          color: on ? "#fdfaf7" : "var(--ink-3)",
                        }}
                      >
                        <Icon className="h-6 w-6" />
                      </span>
                      <div>
                        <h3 className="text-[clamp(1.45rem,1.8vw,1.8rem)] font-semibold tracking-[-0.01em]">
                          {block.title}
                        </h3>
                        <p className="mt-2 text-[clamp(1.15rem,1.3vw,1.35rem)] leading-relaxed text-[color:var(--ink-2)]">
                          {block.body}
                        </p>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------- 3 · Sprachbasierte Entwicklung */}
      <section
        id="biomarker"
        className="relative overflow-hidden py-28 lg:py-40"
        style={{ background: "var(--bg)" }}
      >
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-10">
          <div className="mx-auto max-w-[48rem] text-center">
            <Reveal as="h2" className="t-section">
              {WELLBEING.speech.title.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </Reveal>
            <Reveal as="p" delay={120} className="t-lead mt-6 text-[color:var(--ink-2)]">
              {WELLBEING.speech.subtitle}
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 items-center gap-14 lg:mt-24 lg:grid-cols-[0.85fr_1fr] lg:gap-24">
            <div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {WELLBEING.speech.cards.map((card, i) => {
                  const Icon = ICONS[card.icon] || FiMic;
                  return (
                    <Reveal key={card.title} delay={i * 100}>
                      <article className="card h-full px-[clamp(1.5rem,2vw,2.25rem)] py-[clamp(1.5rem,2vw,2.25rem)]">
                        <span
                          aria-hidden
                          className="grid h-14 w-14 place-items-center rounded-full bg-[color:var(--bg-2)] text-[color:var(--color-highlight)]"
                        >
                          <Icon className="h-6 w-6" />
                        </span>
                        <h3 className="mt-6 text-[clamp(1.0625rem,1.35vw,1.55rem)] font-semibold tracking-[-0.01em]">
                          {card.title}
                        </h3>
                        <p className="mt-2.5 text-[clamp(0.9375rem,1.1vw,1.3rem)] leading-relaxed text-[color:var(--ink-2)]">
                          <Figures text={card.body} mark={card.mark} />
                        </p>
                      </article>
                    </Reveal>
                  );
                })}
              </div>

              <Reveal delay={420}>
                <p className="mt-8 max-w-[30rem] text-[0.875rem] leading-relaxed text-[color:var(--ink-3)]">
                  {WELLBEING.speech.disclaimer}
                </p>
              </Reveal>
            </div>

            <Reveal delay={140} className="order-first lg:order-last">
              <Dashboard
                shot="biomarker"
                alt={WELLBEING.speech.alt}
                className="!max-w-[27rem]"
              />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
